import {
  DailyGridCell,
  DailyGridCellField,
  DailyGridRow,
} from "../../../types/sale-daily-grid.type";
import { VAT_RATE_PERCENT } from "../../../utils/vat";

// ---------------------------------------------------------------------------
// Cell mapping (the core contract). `cells` is sparse and is mapped to the
// `columns` array by product_code — NEVER by index.
// ---------------------------------------------------------------------------

export type CellByCode = Map<string, DailyGridCell>;

/** Build a product_code -> cell lookup for one row. */
export const buildCellByCode = (cells: DailyGridRow["cells"]): CellByCode =>
  new Map(cells.map((cell) => [cell.product_code, cell]));

/**
 * Resolve the three quantities for a (row, product) pair. Missing cell => all
 * zero (the product had no sale / waste / leftover for that row).
 */
export const resolveCellValues = (
  cellByCode: CellByCode,
  productCode: string,
): { sold_qty: number; waste_qty: number; leftover_qty: number } => {
  const cell = cellByCode.get(productCode);
  return {
    sold_qty: cell?.sold_qty ?? 0,
    waste_qty: cell?.waste_qty ?? 0,
    leftover_qty: cell?.leftover_qty ?? 0,
  };
};

// ---------------------------------------------------------------------------
// Stable identity helpers
// ---------------------------------------------------------------------------

/**
 * Stable key for a row across re-renders. Rows are never re-sorted on the FE so
 * the array index is stable, but we fold in business identity so edit-state and
 * React keys survive a re-fetch that keeps the same ordering. `no` alone is not
 * guaranteed unique (off-pathway rows can repeat numbering), hence the combo.
 */
export const getRowKey = (row: DailyGridRow, index: number): string =>
  [
    index,
    row.no,
    row.customer_code,
    row.stop_no ?? "off",
    row.bill_id ?? "nobill",
  ].join("|");

/** Key for a single editable quantity cell. */
export const getEditKey = (
  rowKey: string,
  productCode: string,
  field: DailyGridCellField,
): string => `${rowKey}::${productCode}::${field}`;

// ---------------------------------------------------------------------------
// Frozen (left-locked) column layout. Widths are explicit so the sticky `left`
// offsets and the header/body cells stay aligned (and scale together under the
// table's CSS `zoom`).
// ---------------------------------------------------------------------------

export interface FrozenColumn {
  key:
    | "no"
    | "customer_code"
    | "customer_name"
    | "start_time"
    | "duration_time"
    | "bill_id"
    | "payment_qr"
    | "payment_channel";
  label: string;
  /** Optional second line in the header label. */
  subLabel?: string;
  width: number;
  align: "left" | "center";
}

/**
 * The two payment columns (ข้อ 2.4 ข) sit here, at the END of the leading block
 * and NOT in the right-hand summary block, for three reasons:
 *
 *  1. They are per-bill attributes, so they belong next to เลขบิล rather than
 *     stranded on the far side of the product matrix.
 *  2. `SUMMARY_COLUMNS` is typed `keyof RowMoney` — it is a money strip. These
 *     two carry a status and a label, not an amount.
 *  3. Indices >= FROZEN_STICKY_COUNT scroll rather than pin, so appending here
 *     leaves every sticky `left` offset (and the 2px separator drawn at the last
 *     pinned index) exactly where it was.
 */
export const FROZEN_COLUMNS: FrozenColumn[] = [
  { key: "no", label: "No.", width: 52, align: "center" },
  { key: "customer_code", label: "รหัสร้าน", width: 116, align: "left" },
  { key: "customer_name", label: "ชื่อร้าน", width: 168, align: "left" },
  { key: "start_time", label: "เวลาเข้า", width: 84, align: "center" },
  { key: "duration_time", label: "ระยะเวลา", subLabel: "ระหว่างร้าน", width: 90, align: "center" },
  { key: "bill_id", label: "เลขบิล", subLabel: "Invoice", width: 150, align: "left" },
  // 112px, not the original 78: the option-A wording is far longer than the old
  // "QR ✓" tokens, and the widest string this cell can render is the unknown
  // state "? ไม่ทราบสถานะ" (~91px of glyphs + 16px cell padding). Anything
  // narrower wraps or clips exactly on the row that most needs to be legible.
  // Index >= FROZEN_STICKY_COUNT, so this scrolls and no sticky `left` offset
  // shifts; FROZEN_TOTAL_WIDTH is derived, so it follows automatically.
  { key: "payment_qr", label: "QR", subLabel: "สถานะชำระ", width: 112, align: "center" },
  { key: "payment_channel", label: "ช่องทางชำระ", subLabel: "ทุกประเภท", width: 96, align: "center" },
];

/** Cumulative left offset (px) for each frozen column, by index. */
export const getFrozenLeftOffsets = (cols: FrozenColumn[] = FROZEN_COLUMNS): number[] => {
  const offsets: number[] = [];
  let acc = 0;
  for (const col of cols) {
    offsets.push(acc);
    acc += col.width;
  }
  return offsets;
};

export const FROZEN_TOTAL_WIDTH = FROZEN_COLUMNS.reduce(
  (sum, c) => sum + c.width,
  0,
);

/**
 * Of the leading columns, how many stay PINNED (sticky-left). The remaining
 * leading columns (เวลาเข้า / ระยะเวลา / เลขบิล) scroll with the product matrix
 * instead of staying locked.
 */
export const FROZEN_STICKY_COUNT = 3;

/** Total width (px) of just the pinned leading columns (No. + รหัสร้าน + ชื่อร้าน). */
export const FROZEN_STICKY_WIDTH = FROZEN_COLUMNS.slice(
  0,
  FROZEN_STICKY_COUNT,
).reduce((sum, c) => sum + c.width, 0);

// Product sub-column geometry (the ขาย / เสีย / เหลือ trio under each product).
export const PRODUCT_SUBCOL_WIDTH = 46;
export const PRODUCT_GROUP_WIDTH = PRODUCT_SUBCOL_WIDTH * 3;

export interface SubColumn {
  field: DailyGridCellField;
  label: string;
}

export const PRODUCT_SUBCOLUMNS: SubColumn[] = [
  { field: "sold_qty", label: "ขาย" },
  { field: "waste_qty", label: "เสีย" },
  { field: "leftover_qty", label: "เหลือ" },
];

// ---------------------------------------------------------------------------
// Labels / formatting
// ---------------------------------------------------------------------------

const THAI_FULL_WEEKDAYS = [
  "วันจันทร์",
  "วันอังคาร",
  "วันพุธ",
  "วันพฤหัสบดี",
  "วันศุกร์",
  "วันเสาร์",
  "วันอาทิตย์",
];

/** ISO day-of-week (1 = Monday … 7 = Sunday) -> Thai full weekday. */
export const isoDayToThai = (day: number | null | undefined): string => {
  if (!day || day < 1 || day > 7) return "-";
  return THAI_FULL_WEEKDAYS[day - 1];
};

/** All seven weekdays for the decorative rail, in display order Mon..Sun. */
export const THAI_WEEKDAY_RAIL = THAI_FULL_WEEKDAYS.map((label, i) => ({
  label,
  isoDay: i + 1,
}));

// ---------------------------------------------------------------------------
// Thai weekday colours (the canonical สีประจำวัน). ISO day 1 = Monday … 7 =
// Sunday. `bg`/`fg` are the saturated chip (the report's own day); `pale` tints
// the inactive chips so the rail reads as a colour key without shouting.
// ---------------------------------------------------------------------------

export interface DayColor {
  bg: string;
  fg: string;
  pale: string;
}

export const THAI_DAY_COLORS: Record<number, DayColor> = {
  1: { bg: "#E7B91C", fg: "#4a3b00", pale: "#FBF1C9" }, // จันทร์ — เหลือง
  2: { bg: "#F15C9A", fg: "#ffffff", pale: "#FBD9E7" }, // อังคาร — ชมพู
  3: { bg: "#3FA34D", fg: "#ffffff", pale: "#D6EBD9" }, // พุธ — เขียว
  4: { bg: "#EE8B27", fg: "#ffffff", pale: "#FBE3CC" }, // พฤหัส — ส้ม
  5: { bg: "#4F9BE8", fg: "#ffffff", pale: "#D6E6FA" }, // ศุกร์ — ฟ้า
  6: { bg: "#8A79C6", fg: "#ffffff", pale: "#E2DEF0" }, // เสาร์ — ม่วง
  7: { bg: "#E5534B", fg: "#ffffff", pale: "#FAD7D5" }, // อาทิตย์ — แดง
};

const NEUTRAL_DAY_COLOR: DayColor = {
  bg: "#ededf3",
  fg: "#8a8a8a",
  pale: "#f3f3f6",
};

export const getDayColor = (isoDay: number | null | undefined): DayColor =>
  (isoDay != null && THAI_DAY_COLORS[isoDay]) || NEUTRAL_DAY_COLOR;

// Keyed UPPER_SNAKE. The API actually sends the stored enum — lowercase with
// SPACES ("branch account approved") — so `statusKey` normalises both shapes;
// upper-casing alone left every in-between status reading as English.
// Unknown values fall back to a humanized form of the raw status.
const DAILY_GRID_STATUS_LABEL: Record<string, string> = {
  DRAFT: "ฉบับร่าง",
  BRANCH_FINANCE_SUBMITTED: "รอบัญชีตรวจสอบ",
  BRANCH_ACCOUNT_APPROVED: "รอ HO ตรวจสอบ",
  BRANCH_ACCOUNT_REQUEST_CHANGE: "บัญชีขอแก้ไข",
  HO_REQUEST_CHANGE: "รอการแก้ไข",
  HO_APPROVED: "HO ตรวจสอบแล้ว",
  REJECTED: "ไม่อนุมัติ",
};

const DAILY_GRID_STATUS_COLOR: Record<string, string> = {
  DRAFT: "#D9D9D9",
  BRANCH_FINANCE_SUBMITTED: "#FDA42C",
  BRANCH_ACCOUNT_APPROVED: "#FDA42C",
  BRANCH_ACCOUNT_REQUEST_CHANGE: "#5C69E8",
  HO_REQUEST_CHANGE: "#5C69E8",
  HO_APPROVED: "#45BE77",
  REJECTED: "#FB3B3B",
};

const statusKey = (status: string): string =>
  status.trim().toUpperCase().replace(/\s+/g, "_");

export const getStatusLabel = (status: string): string => {
  if (!status) return "-";
  const key = statusKey(status);
  return (
    DAILY_GRID_STATUS_LABEL[key] ??
    // humanize an unknown raw status: "SOME_STATUS" -> "Some status"
    status
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase())
  );
};

export const getStatusColor = (status: string): string =>
  DAILY_GRID_STATUS_COLOR[statusKey(status ?? "")] ?? "#6a6a6a";

// ---------------------------------------------------------------------------
// Zoom — applied ONLY to the scrollable product matrix (column widths + cell
// fonts). Frozen columns, header row heights, and footer labels are NOT scaled,
// so the locked area stays a fixed size while the product matrix zooms.
// ---------------------------------------------------------------------------

export const ZOOM_MIN = 0.75;
export const ZOOM_MAX = 1.5;
export const ZOOM_STEP = 0.25;
export const ZOOM_DEFAULT = 1;

export const clampZoom = (value: number): number =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100));

/** Scaled product sub-column width (px) for the current zoom. */
export const scaledSubColWidth = (zoom: number): number =>
  Math.round(PRODUCT_SUBCOL_WIDTH * zoom);

/** Faint alternating column tint per product group (Excel-style striping). */
export const productGroupTint = (index: number): string =>
  index % 2 === 0 ? "#FCF7F4" : "#F4F8FC";

// ---------------------------------------------------------------------------
// Money decomposition helpers for the right-hand summary columns (มูลค่าสินค้า /
// ภาษี / รวมยอดขาย).
//
// NOTE: per-bill money is now supplied by the daily-grid API straight from the
// transaction (row.amount_total / vat_total / net_total on each DailyGridRow);
// DailyGrid.tsx uses those directly. The face-price derivation below (deriving
// money from `sold_qty × sell_price` and splitting VAT 7/107) is NO LONGER used
// for per-bill rows — it produced figures based on the PB031 หน้าซอง price
// rather than the actual sale. The helpers are kept only for their unit tests
// and any incidental reuse; do not reintroduce them for per-bill money.
// ---------------------------------------------------------------------------

/** Thai VAT rate as a fraction. Sale prices are treated as VAT-inclusive. */
export const VAT_RATE = VAT_RATE_PERCENT / 100;

/** VAT embedded inside a VAT-inclusive total. */
export const vatOfInclusive = (inclusiveTotal: number): number =>
  inclusiveTotal * (VAT_RATE / (1 + VAT_RATE));

export type PaymentBucket = "cash" | "credit";

/**
 * Map a row's `bill_type` to its payment bucket. The backend enum is
 * `cashsale | invoice | sales_order`; only `cashsale` is เงินสด, the rest are
 * treated as เงินเชื่อ (credit).
 */
export const billPaymentBucket = (
  billType: string | null | undefined,
): PaymentBucket =>
  (billType ?? "").trim().toLowerCase() === "cashsale" ? "cash" : "credit";

// ---------------------------------------------------------------------------
// Payment columns (ข้อ 2.4 ข)
//
// Column 1 (`payment_qr`) is a colour-coded swatch. It answers TWO questions at
// once — has this bill been registered for QR payment, and has it been paid:
//     ชมพู   ลงทะเบียน QR แล้ว และชำระแล้ว
//     แดง    ลงทะเบียน QR แล้ว แต่ยังค้างจ่าย
//     ขาว    ขายสด ที่ยังไม่ได้ลงทะเบียน QR — เป็น "โอกาส" ที่ผลักดันได้
//     เหลือง ขายเชื่อ — ยังไม่มีแนวทางชำระผ่าน QR เลย
//     ลายทางเทา  อ่านสถานะไม่ออก — ต้องตรวจสอบ ไม่ใช่สีในชุด 4 สี
// Column 2 (`payment_channel`) is a plain channel label covering EVERY bill.
//
// White vs yellow is a REAL distinction, not a leftover bucket: white is a cash
// sale that could be pushed onto QR (a sales opportunity), yellow is credit,
// which has no QR path today and so will never be registered.
//
// NOTE: production currently has ZERO QR_CODE rows — the QR feature has not
// shipped. This is built ahead of it; an all-white/yellow column today is the
// expected output, not a bug.
// ---------------------------------------------------------------------------

/**
 * The four swatch states, plus `none` for a row with no bill at all and
 * `qr-unknown` for a bill whose payment we cannot read.
 *
 * `qr-unknown` exists so an unreadable value is never quietly rounded to a
 * plausible one. Guessing "รอชำระ" for a status we do not recognise hides a
 * data problem until it causes a wrong decision downstream; showing it loudly
 * gets it fixed.
 */
export type PaymentQrState =
  | "qr-paid"
  | "qr-pending"
  | "cash-other"
  | "credit"
  | "qr-unknown"
  | "none";

/** payment_type -> Thai channel label for column 2. */
const PAYMENT_CHANNEL_LABELS: Record<string, string> = {
  QR_CODE: "QR Code",
  CASH: "เงินสด",
  BANK_TRANSFER: "โอนเงิน",
  CREDIT: "เครดิต",
};

/**
 * Classify a row for the QR swatch.
 *
 * ขายเชื่อ is decided by `billPaymentBucket(bill_type)`, the same helper the
 * money columns use; `payment_type === "CREDIT"` is accepted as a fallback for
 * rows where bill_type is missing. Everything else is a cash sale, split by
 * payment channel and then by settlement status.
 *
 * Two paths lead to `qr-unknown`, and both are cases where any other answer
 * would be a fabrication:
 *
 *  - a QR bill whose `payment_status` we cannot interpret. Paid and unpaid are
 *    the two things this column exists to tell apart, so picking one at random
 *    is worse than admitting we do not know.
 *  - a bill carrying a `payment_type` that is PRESENT but unrecognised — a new
 *    channel shipped after this build. Calling it "รอลง QR" would assert it is
 *    a cash sale awaiting registration, which we have no basis for.
 *
 * A NULL/absent `payment_type` is deliberately NOT unknown: legacy bills and
 * pre-migration reports genuinely have no channel, and they are cash sales that
 * really are un-registered, so `cash-other` is the honest answer for them.
 */
export const paymentQrState = (row: {
  bill_id?: string | null;
  bill_type?: string | null;
  payment_type?: string | null;
  payment_status?: string | null;
}): PaymentQrState => {
  // No bill on this row (empty pathway stop) — nothing was paid either way.
  if (!row.bill_id) return "none";

  // Reuse the grid's existing cash/credit split so this column can never
  // disagree with the เงินสด / เงินเชื่อ summary columns. It also correctly
  // treats `sales_order` (not just `invoice`) as credit.
  const paymentType = (row.payment_type ?? "").toUpperCase();
  if (billPaymentBucket(row.bill_type) === "credit" || paymentType === "CREDIT") {
    return "credit";
  }

  if (paymentType === "QR_CODE") {
    const status = (row.payment_status ?? "").toLowerCase();
    if (status === "paid") return "qr-paid";
    if (status === "pending") return "qr-pending";
    return "qr-unknown";
  }

  // A channel we have never heard of — flag it rather than bucketing it.
  if (paymentType !== "" && PAYMENT_CHANNEL_LABELS[paymentType] === undefined) {
    return "qr-unknown";
  }

  // ขายสด ที่ยังไม่ได้ลงทะเบียน QR (รวมถึงบิลเก่าที่ไม่มี payment_type).
  return "cash-other";
};

/**
 * Short in-cell token + full tooltip per swatch state.
 *
 * The token exists so the cell is not carrying its meaning in colour alone —
 * colour-blind and greyscale-printed readers get the same information.
 */
export const PAYMENT_QR_LABELS: Record<
  PaymentQrState,
  { text: string; tooltip: string }
> = {
  "qr-paid": {
    text: "QR จ่ายแล้ว",
    tooltip: "ลงทะเบียน QR Code แล้ว และชำระเงินแล้ว",
  },
  "qr-pending": {
    text: "QR ค้างจ่าย",
    tooltip: "ลงทะเบียน QR Code แล้ว แต่ยังไม่ชำระเงิน",
  },
  // ขาว = โอกาสในการขาย: บิลนี้ยังผลักดันให้ชำระผ่าน QR ได้
  "cash-other": {
    text: "รอลง QR",
    tooltip: "ขายสด ยังไม่ได้ลงทะเบียน QR Code — ผลักดันให้ชำระผ่าน QR ได้",
  },
  // เหลือง = ไม่ใช่กลุ่มเป้าหมาย: ขายเชื่อยังไม่มีเส้นทางชำระผ่าน QR
  credit: {
    text: "ไม่ลง QR",
    tooltip: "ขายเชื่อ ยังไม่มีแนวทางชำระผ่าน QR Code",
  },
  "qr-unknown": {
    text: "? ไม่ทราบสถานะ",
    tooltip: "อ่านสถานะการชำระเงินไม่ได้ — กรุณาตรวจสอบข้อมูลบิลนี้",
  },
  none: { text: "", tooltip: "" },
};

/**
 * Tooltip for the swatch, including the RAW value when it could not be read.
 *
 * Naming the offending value is the entire point of the `qr-unknown` state: a
 * generic "unknown" tells whoever investigates nothing, whereas
 * `สถานะที่ได้รับ: "xyz"` points straight at the producer of the bad data.
 */
export const paymentQrTooltip = (row: {
  bill_id?: string | null;
  bill_type?: string | null;
  payment_type?: string | null;
  payment_status?: string | null;
}): string => {
  const state = paymentQrState(row);
  const base = PAYMENT_QR_LABELS[state].tooltip;
  if (state !== "qr-unknown") return base;

  // Which half is unreadable decides which value is worth naming.
  const paymentType = (row.payment_type ?? "").toUpperCase();
  const received =
    paymentType === "QR_CODE"
      ? `สถานะที่ได้รับ: "${row.payment_status ?? ""}"`
      : `ช่องทางที่ได้รับ: "${row.payment_type ?? ""}"`;
  return `${base} (${received})`;
};

/**
 * Column 2 — "ระบุช่องทางชำระเงินจริง", every bill type.
 *
 * Three outcomes that must stay visually distinct:
 *
 *  - `-`        no bill on this row. Not a data gap, just an empty stop.
 *  - `ไม่ระบุ`   a bill exists but its channel is genuinely ABSENT (NULL) —
 *               legacy bills and reports built before the column existed.
 *  - the raw value, flagged: a channel that is PRESENT but unrecognised.
 *
 * The last case used to collapse into `ไม่ระบุ`, which meant that the day a new
 * payment channel shipped, every bill using it would read as "no channel
 * recorded" and nobody would notice. `isUnrecognised` drives a loud rendering
 * and the raw value is shown as-is so it can be traced.
 */
export type PaymentChannelDisplay = {
  text: string;
  /** True ONLY for a present-but-unrecognised value — never for NULL. */
  isUnrecognised: boolean;
  tooltip: string;
};

export const paymentChannelDisplay = (row: {
  bill_id?: string | null;
  payment_type?: string | null;
}): PaymentChannelDisplay => {
  if (!row.bill_id) return { text: "-", isUnrecognised: false, tooltip: "" };

  const raw = row.payment_type ?? "";
  if (raw === "") {
    return {
      text: "ไม่ระบุ",
      isUnrecognised: false,
      tooltip: "บิลนี้ไม่มีข้อมูลช่องทางชำระเงิน (บิลระบบเก่า หรือรายงานก่อนเพิ่มคอลัมน์นี้)",
    };
  }

  const known = PAYMENT_CHANNEL_LABELS[raw.toUpperCase()];
  if (known !== undefined) return { text: known, isUnrecognised: false, tooltip: known };

  return {
    text: raw,
    isUnrecognised: true,
    tooltip: `ไม่รู้จักช่องทางชำระเงินนี้ — กรุณาตรวจสอบ (ช่องทางที่ได้รับ: "${raw}")`,
  };
};

/**
 * Plain-text channel label. Thin wrapper over `paymentChannelDisplay` for
 * callers that only need the string (exports, tests).
 */
export const paymentChannelLabel = (row: {
  bill_id?: string | null;
  payment_type?: string | null;
}): string => paymentChannelDisplay(row).text;


export interface RowMoney {
  /** Ex-VAT goods value billed on credit (เงินเชื่อ). */
  creditValue: number;
  /** Ex-VAT goods value paid in cash (เงินสด). */
  cashValue: number;
  /** Embedded VAT (ภาษี). */
  vat: number;
  /** VAT-inclusive sale total (รวมยอดขาย). */
  totalSale: number;
}

/**
 * Decompose a row's VAT-inclusive sale total into the four summary figures,
 * routing the ex-VAT value into cash/credit by the bill's payment type.
 */
export const computeRowMoney = (
  saleInclusiveTotal: number,
  billType: string | null | undefined,
): RowMoney => {
  const vat = vatOfInclusive(saleInclusiveTotal);
  const exVat = saleInclusiveTotal - vat;
  const cash = billPaymentBucket(billType) === "cash";
  return {
    creditValue: cash ? 0 : exVat,
    cashValue: cash ? exVat : 0,
    vat,
    totalSale: saleInclusiveTotal,
  };
};

// Right-hand summary columns (scrollable, NOT frozen — they follow the product
// matrix in the reference). `vat`/`total_sale` stand alone; `credit`/`cash` sit
// under the "มูลค่าสินค้า" group header.
export interface SummaryColumn {
  key: keyof RowMoney;
  label: string;
  width: number;
  /** Sits under the มูลค่าสินค้า group header (vs. standing alone). */
  underValueGroup: boolean;
}

export const SUMMARY_VALUE_GROUP_LABEL = "มูลค่าสินค้า";

export const SUMMARY_COLUMNS: SummaryColumn[] = [
  { key: "creditValue", label: "เงินเชื่อ", width: 84, underValueGroup: true },
  { key: "cashValue", label: "เงินสด", width: 84, underValueGroup: true },
  { key: "vat", label: "ภาษี", width: 70, underValueGroup: false },
  { key: "totalSale", label: "รวมยอดขาย", width: 104, underValueGroup: false },
];

export const SUMMARY_TOTAL_WIDTH = SUMMARY_COLUMNS.reduce(
  (sum, c) => sum + c.width,
  0,
);

/**
 * Cumulative RIGHT offset (px) for each summary column, by index. Mirrors
 * `getFrozenLeftOffsets` but measured from the right edge, so the summary
 * columns can be `position: sticky; right: offset[i]` (locked to the right,
 * like the reference) instead of scrolling away with the product matrix.
 * offset[i] = Σ widths of the columns AFTER i (the rightmost column = 0).
 */
export const getSummaryRightOffsets = (
  cols: SummaryColumn[] = SUMMARY_COLUMNS,
): number[] => {
  const offsets: number[] = new Array(cols.length).fill(0);
  let acc = 0;
  for (let i = cols.length - 1; i >= 0; i--) {
    offsets[i] = acc;
    acc += cols[i].width;
  }
  return offsets;
};

// ---------------------------------------------------------------------------
// Edit validation
// ---------------------------------------------------------------------------

/**
 * Validate raw input for an editable quantity cell. Numeric, non-negative.
 * Empty string is allowed transiently (the caller decides the commit fallback).
 */
export const isValidQtyInput = (raw: string): boolean => {
  if (raw.trim() === "") return true;
  if (!/^\d*\.?\d*$/.test(raw)) return false;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0;
};
